import { HttpHeaders, HttpParams } from "@angular/common/http";
import { INITIALISATION_TYPE_URL_PARAM, METER_ID_URL_PARAM, UtilityConstants, ZONE_NAME_URL_PARAM } from "../constants/utiliyconstants";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { PaymentType } from "../models/basic.models";
import { addMinutes, format, subMinutes } from 'date-fns';
import { cloneDeep } from 'lodash';
import { NgZone } from "@angular/core";

export default class Utils {

    static sleep(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    static hexToRgb(hex: string) {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
        } : null;
    }

    static isMultiDayParking(minStay: number, maxStay: number): boolean {
        return (minStay >= UtilityConstants.MINUTES_IN_DAY)
            && (minStay === UtilityConstants.MINUTES_IN_DAY || minStay % UtilityConstants.MINUTES_IN_DAY === 0)
            && (maxStay % UtilityConstants.MINUTES_IN_DAY === 0);
    }

    static isURL(text : string) : boolean {
        if(text) {
            const regex = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/
            if(text.match(regex)) return true
        }

        return false
    }

    static setRecaptchaHeader(recaptchaToken: string | null = null, existingHeaders: HttpHeaders | null = null): HttpHeaders | null {
        if (recaptchaToken == null) {
            return existingHeaders;
        }

        let result = existingHeaders != null ? existingHeaders : new HttpHeaders();
        result = result.set("Pz-GoogleCaptcha", recaptchaToken);
        return result;
    }

    /**
     * Adds an offset in minutes to a time (or now).
     * @returns DateTime with offset applied.
     */
    static getTimeAtOffset(offsetMinutes: number, time: Date | null = null): Date {
        if (time == null) {
            time = new Date();
        }

        if (offsetMinutes == null) {
            return time;
        }

        return addMinutes(time, offsetMinutes);
    }

    /**
     * Removes an offset at a time (or now) to represent that time at the home zone (from where the offset is originally calculated).
     * @returns DateTime at the home zone.
     */
    static getTimeAtHomeZone(offsetMinutes: number, time: Date | null = null): Date {
        if (time == null) {
            time = new Date();
        }

        if (offsetMinutes == null) {
            return time;
        }

        return subMinutes(time, offsetMinutes);
    }

    /**
     * Generates a standard GUID string.
     * @link https://stackoverflow.com/questions/42782645/how-to-create-guid-in-angular-2
     */
    static generateGuid(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            let r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    /**
     * Takes a string of parameters as found in a URL and returns an equivalent object variant.
     * @example "param1=123&param2=abc" -> { param1: 123, param2: abc }
     */
    static queryParameterStringToObject(parametersString: string): {} {
        const params: Params = {};
        parametersString?.split("&").forEach(parameter => {
          const key = parameter.split("=")[0];
          const value = parameter.split("=")[1];
          params[key] = value;
        });
        return params;
    }

    /** Stringifies a Params object and stores in a single key on sessionStorage. */
    static saveQueryParametersToSessionStorage(params: Params, key: string) {
        if (Object.keys(params).length > 0) {
            sessionStorage.setItem(key, JSON.stringify(params));
        }
    }
    /** Returns a stringified Params object from sessionStorage at a given key.
     *  @returns Params object if found and can be cast as Params. Will also remove key from session storage. Else returns null.
     */
    static loadQueryParametersFromSessionStorage(key: string): Params | null {
      const storageItem = sessionStorage.getItem(key);

      if (!storageItem) {
        return null;
      }

      try {
        const storedParameters = JSON.parse(storageItem) as Params;
        sessionStorage.removeItem(key);

        return storedParameters;
      }
      catch {
        return null;
      }
    }

    /**
     *
     *
     * @static
     * @template T
     * @param {(T[] | null)} array
     * @return {*}  {boolean}
     * @memberof Utils
     */
    static checkArrayNullOrEmpty<T>(array: T[] | null): boolean {
        return array === undefined || array === null || array.length == 0;
    }


    static isIphone(): boolean {
        return /iPad|iPhone|iPod|((AppleWebKit)(?!.*Safari))/i.test(navigator.userAgent);
    }

    static getParkingParams(zoneName: string | undefined, meterId: string | undefined, initialisationType: string | undefined): Params {
        return {
          [ZONE_NAME_URL_PARAM]: zoneName,
          [METER_ID_URL_PARAM]: meterId,
          [INITIALISATION_TYPE_URL_PARAM]: initialisationType
        };
    }

    /** Converts a Params object into a string representation for a URL query, ignoring undefined values. */
    static parkingParamsToString(params: Params): String {

        if (!params) {
            return "";
        }

        const noUndefinedValuesParams: Params = Object.keys(params).reduce((queryParams: Params, key) => {
            let value: any = params[key];
            value = Array.isArray(value) ? value[0] : value;

            if (value !== undefined) {
              queryParams[key] = value;
            }

            return queryParams;
          }, {}
        );

        const httpParams: HttpParams = new HttpParams({ fromObject: noUndefinedValuesParams });
        return httpParams.toString();
    }

    static isAndroid(): boolean {
        return /Android\s[0-9\.]+;/i.test(navigator.userAgent);
    }

    static paymentTypeShouldHideNumber(paymentType: PaymentType): boolean {
        return paymentType == "APPLE"
            || paymentType == "APPLEPAYWEB"
            || paymentType == "GOOGLE"
            || paymentType == "ZIP_MONEY";
    }
}

export const notConcurrent = <T>(proc: () => PromiseLike<T>) => {
    let inFlight: Promise<T> | false = false;
    return () => {
        if (!inFlight) {
            inFlight = (async () => {
                try {
                    return await proc();
                } finally {
                    inFlight = false;
                }
            })();
        }

        return inFlight;
    };
};

export class Mutex {

    private mutex = Promise.resolve();
    lock():
    PromiseLike<() => void> {
        let begin: (unlock: () => void) => void = unlock => {};
        this.mutex = this.mutex.then(() => {
            return new Promise(begin);
        });
        return new Promise(res => { begin = res; });
    }

    async dispatch<T>(fn: (() => T) | (() => PromiseLike<T>)): Promise<T> {
        const unlock = await this.lock();
        try {
            return await Promise.resolve(fn());
        } finally {
            unlock();
        }
    }
}

class SampleMutexUsage {
    readonly collectionMutex = new Mutex();

    async set(collection: string, key: string, value: string): Promise<string> {
        return await this.collectionMutex.dispatch(async () => {
            const data = await Promise.resolve();
            await Promise.resolve();
            return 'data';
        });
    }
}

/**
 * Takes a keypress event and returns true if its keycode is a digit. Often used with
 * (keypress) events of <input> elements.
 * @param event $event object emitted by the keypress
 * @param whitelist array of character codes that are also allowed
 * @returns true if event's keyCode represents a digit, otherwise false
 */
export const numberOnly = (event: { which: any; keyCode: any; }, whitelist: number[] = []): boolean => {
    const charCode = (event.which) ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57) && !whitelist.includes(charCode)) {
      return false;
    }
    return true;
};

/**
 * Takes a keypress event and returns true if its keycode is a digit or letter. Often used with
 * (keypress) events of <input> elements.
 * @param event $event object emitted by the keypress
 * @returns true if event's keyCode represents an alphanumeric character, otherwise false
 */
export const alphaNumericOnly = (event: { which: any; keyCode: any; }): boolean => {
    const keyCode = (event.which) ? event.which : event.keyCode;
    const regex: RegExp = /^[a-zA-Z0-9]+$/;
    if (!(String.fromCharCode(keyCode)).match(regex)) {
        return false;
    }
    return true;
};

/**
 * Divides a string into an array of strings based on a chunk size.
 * @param str String to split
 * @param chunkSize Maximum number of characters allowed in any given substring
 * @returns string[] from str with each element not longer than chunkSize characters or an
 *  empty array if parameters not valid.
 * @link https://stackoverflow.com/a/29202760
 */
 export function chunkSubstring(str: string, chunkSize: number): string[] {
    if (chunkSize < 1) { return []; }
    if (str == null) { return []; }

    const elementCount = Math.ceil(str.length / chunkSize);
    const result: string[] = new Array(elementCount);

    for (let i = 0, prevStringIndex = 0; i < elementCount; i++, prevStringIndex += chunkSize) {
      result[i] = str.substring(prevStringIndex, prevStringIndex + chunkSize)
    }

    return result;
  }

export const nameof = <T>(name: keyof T) => name;

export class PasswordUtils {

    static passwordValidationCheck(password : string, username : string) : boolean {
        return this.passwordValidationCheckPayStay(password, username);
    }

    // static passwordValidationCheckAT(password : string, username : string) : boolean {

    //   let passwordComplexity = 0;

    //   // Complexity check tslint:disable-next-line:curly
    //   // tslint:disable-next-line:curly
    //   if (/[a-z]+/.test(password))
    //     passwordComplexity++;

    //   // tslint:disable-next-line:curly
    //   if (/[A-Z]+/.test(password))
    //     passwordComplexity++;

    //   // tslint:disable-next-line:curly
    //   if (/\w{5,}/.test(password))
    //     passwordComplexity++;

    //   // tslint:disable-next-line:curly
    //   if (passwordComplexity < 3)
    //     return false;

    //   // tslint:disable-next-line:curly
    //   if (password.indexOf(username) >= 0)
    //     return false;

    //   // Username check
    //   return true;

    // }

    static passwordValidationCheckPayStay(password : string, username : string) : boolean {
      let passwordComplexity = 0;
      // // Valid Charcter check tslint:disable-next-line:curly
      for (let i = 0; i < password.length; i++) {
        if (!( (/\d+/.test(password[i])) || (/[a-z]+/.test(password[i])) || (/[A-Z]+/.test(password[i])) )) {
          if (! /(?=.*[€!@%^&*$\-()~#|+.?{}()[\]<>/'])/.test(password[i])) {
            return false;
          }
        }
      }

      // Complexity check tslint:disable-next-line:curly
      // tslint:disable-next-line:curly
      if (/\d+/.test(password))
        passwordComplexity++;
      // tslint:disable-next-line:curly
      if (/[a-z]+/.test(password))
        passwordComplexity++;

      // tslint:disable-next-line:curly
      if (/[A-Z]+/.test(password))
        passwordComplexity++;

      // tslint:disable-next-line:curly
      if (/[€!@%^&*$()~#+|.?{}\-()[\]<>/']+/.test(password))
        passwordComplexity++;

      // tslint:disable-next-line:curly
      if (passwordComplexity < 3)
        return false;

      // tslint:disable-next-line:curly
      if (password.indexOf(username) >= 0)
        return false;

      // Username check tslint:disable-next-line:curly
      // tslint:disable-next-line:curly
      if (password.length < 8)
        return false;
      return true;

    }

    static randomPasswordGenerator(): string {
        let result = '';
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const alphaCharacters = 'abcdefghijklmnopqrstuvwxyz';
        const charactersLength = characters.length;
        const alphacCharLength = alphaCharacters.length;
        for ( let i = 0; i < 20; i++ ) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
        }
        const randomStr = alphaCharacters.charAt(Math.floor(Math.random() * alphacCharLength)).toUpperCase()
                + result + Math.floor(Math.random() * charactersLength) +
                alphaCharacters.charAt(Math.floor(Math.random() * alphacCharLength));

        return randomStr;
    }

}

/**
 * This removeParamFromUrl function can be used to remove param string from the URL link.
 * @param router Angular Router instance
 * @param route Angular ActivatedRoute instance
 * @param param parameter to be removed
 * @param callback Optional function to execute after the URL has been updated.
 */
export function removeParamFromUrl(
    router: Router,
    route: ActivatedRoute,
    param: string,
    callback?: () => void
): void {
    const parameters = cloneDeep(route.snapshot.queryParams);

    // if param does not exist in the parameters
    if (!(param in parameters)) {
        if (callback) callback();
        return;
    }

    delete parameters[param];
    router.navigate([], {
        queryParams: parameters,
        replaceUrl: true,
        skipLocationChange: false
    }).then(() => {
        if (callback) callback();
    }).catch(err => {
        console.error('Failed to remove param from URL:', err);
    });
}

/**
 * This addParamToUrl function can be used to add param string from the URL link.
 * @param router Angular Router instance
 * @param route Angular ActivatedRoute instance
 * @param param parameter to be added
 * @param val value to be added to the parameter
 * @param callback Optional function to execute after the URL has been updated.
 */
export function addParamToUrl(
    router: Router,
    route: ActivatedRoute,
    param: string,
    val: string,
    callback?: () => void
): void {
    const parameters = cloneDeep(route.snapshot.queryParams);

    if (parameters[param] == val) {
        if (callback) callback();
        return;
    }

    parameters[param] = val;
    router.navigate([], {
        queryParams: parameters,
        replaceUrl: true,
        skipLocationChange: false
    }).then(() => {
        if (callback) callback();
    }).catch(err => {
        console.error('Failed to add param to URL:', err);
    });
}

/**
 * Remove multiple query params from the URL at once
 * @param router Angular Router
 * @param route ActivatedRoute
 * @param params Array of param names to remove
 */
export async function removeMultipleQueryParams(
  router: Router,
  route: ActivatedRoute,
  params: string[],
  ngZone: NgZone
): Promise<Record<string, any>> {
  const updatedParams = cloneDeep(route.snapshot.queryParams);

  // Remove specified params from the cloned object
  params.forEach(param => delete updatedParams[param]);

  try {
    await ngZone.run(() =>
      router.navigate([], {
        queryParams: updatedParams,
        replaceUrl: true,
        skipLocationChange: false
      })
    );
    return updatedParams;
  } catch (error) {
    throw error;
  }
}

// Note: If a string date is passed, it has to be a valid ISO 8601.
export function getLocaleDate(
    date: Date | string,
    localeCountry: string,
    style: "medium" | "short" = "short",
    timeZone?: string
): string {
  const options: Intl.DateTimeFormatOptions =
    style === "medium"
      ? { year: "numeric", month: "short", day: "numeric" }
      : { year: "numeric", month: "numeric", day: "numeric" };
      
  if (timeZone) {
    options.timeZone = timeZone;
  }

  date = typeof date === "string" ? new Date(date) : date;

  return date.toLocaleDateString(localeCountry, options);
}

export function formatDuration(minutes: number): string {
    const days = Math.floor(minutes / (60 * 24));
    const hours = Math.floor((minutes % (60 * 24)) / 60);
    const mins = minutes % 60;

    let parts: string[] = [];

    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (mins > 0 || parts.length === 0) parts.push(`${mins}m`);

    return parts.join(' ');
}

export function formatInvoiceFileName(invoiceName: string, date: Date): string {
    const formattedDate = format(date, "yyyy-MM-dd HHmm");
    var combinedInvoiceName = `${invoiceName}_${formattedDate}`;
    var formattedInvoiceName = combinedInvoiceName.replace(/\s/g, "_");
    return `${formattedInvoiceName}.pdf`;
}

export function getValidDateStringOrUndefined(dateString: string | undefined | null): string | undefined {
    if (dateString === undefined || dateString === null) {
        return undefined;
    }

    return new Date(dateString).toString() !== 'Invalid Date' ? dateString : undefined;
}